/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import AccountService from 'cio/services/account';
import AuthService from 'cio/services/auth';
import TemplateModel from 'cio/models/template';
import ActionModel from 'cio/models/action';
import { Platform } from 'cio/utils/push/platforms';
import { RequestMethod } from 'cio/services/api';
import { ComposerEditableEntityAnalyticsData } from 'cio/utils/composer/composer-editable-entity';

interface SendTestArgs {
  action: ActionModel;
  template: TemplateModel;
  preview: unknown;
  preprocessor: 'none' | 'premailer';
  isDisabled: boolean;
  placeholder: string;
  type: string;
  requestMethod: RequestMethod;
  recipient: string;
  prependTest: boolean;
  platform: Platform;
  selectedEvent: string;
  selectedCustomer: string;
  toggleSendTestModal: ({
    recipient,
    prependTest,
    platform,
  }: Pick<SendTestArgs, 'recipient' | 'prependTest' | 'platform'>) => unknown;
  isShowingSendTestModal: boolean;
  analyticsData?: ComposerEditableEntityAnalyticsData;
}

export default class SendTest extends Component<SendTestArgs> {
  @service auth!: AuthService;
  @service account!: AccountService;

  @tracked platform: Platform;
  @tracked recipient: string;
  @tracked prependTest: boolean;

  constructor(owner: unknown, args: SendTestArgs) {
    super(owner, args);

    this.recipient = this.isDefaultRecipientCurrentUser ? this.auth.user?.email ?? 'Unknown recipient' : args.recipient;
    this.prependTest = args.prependTest ?? true;
    this.platform = args.platform;
  }

  get isRecipientReadOnly() {
    return this.args.type === 'email' && !this.account.current.verificationApproved;
  }

  @action
  onPlatformChange(platform: Platform) {
    this.platform = platform;
  }

  @action
  onRecipientChange(recipient: string) {
    this.recipient = recipient;
  }

  @action
  toggleSendTestModalAction() {
    let { recipient, prependTest, platform } = this;
    this.args.toggleSendTestModal({ recipient, prependTest, platform });
  }

  // For email messages:
  //
  // (a) Unverified accounts can send only to themselves, so we set their email as the default recipient
  // (b) If there's no recipient, we set the current user's email as the default recipient.
  private get isDefaultRecipientCurrentUser() {
    return this.args.type === 'email' && (this.isRecipientReadOnly || isEmpty(this.args.recipient));
  }
}
